import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, LinearProgress, Pagination, Stack, Typography } from "@mui/material";
import { COLOR_CONSTANTS } from "@/helpers/Colors";
import { useIsMobile } from "@/hooks/useIsMobile";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";
import { useRfqList } from "./api/rfqQueries";
import MobileRfqList from "./components/MobileRfqList";
import RfqBucketSidebar from "./components/RfqBucketSidebar";
import RfqCard from "./components/RfqCard";
import { RFQ_PAGE_SIZE, bucketMeta } from "./constants/buckets";
import { rfqDetailPath } from "./constants/paths";
import { useNow } from "./hooks/useNow";
import { useRfqBucketParam } from "./hooks/useRfqBucketParam";
import { rfqLoadErrorMessage } from "./utils/apiError";
import type { VendorRfqSummary } from "./types";

export default function RfqList() {
  const { bucket, page, selectBucket, setPage } = useRfqBucketParam();
  const isMobile = useIsMobile();
  const now = useNow();
  const navigate = useNavigate();
  const { data, isLoading, isError, isFetching, error, refetch } = useRfqList({ bucket, page, limit: RFQ_PAGE_SIZE });
  const counts = data?.counts ?? {};
  const totalCount = data ? counts[bucket] ?? data.total : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / RFQ_PAGE_SIZE));

  useEffect(() => {
    if (data && !isFetching && page > totalPages) setPage(totalPages, { replace: true });
  }, [data, isFetching, page, totalPages, setPage]);

  if (isLoading) return <LoaderV2 />;
  if (isError || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography role="alert" sx={{ mb: 1.5 }}>
          {rfqLoadErrorMessage(error, "Could not load RFQs. Please try again.")}
        </Typography>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  const rows = data.items;
  const meta = bucketMeta(bucket);
  const openRfq = (rfq: VendorRfqSummary) => navigate(rfqDetailPath(rfq.id));

  if (isMobile) {
    return (
      <MobileRfqList
        rfqs={rows}
        bucket={bucket}
        counts={counts}
        totalCount={totalCount}
        page={page}
        pageSize={RFQ_PAGE_SIZE}
        isFetching={isFetching}
        now={now}
        onOpen={openRfq}
        onBucketChange={selectBucket}
        onPageChange={setPage}
      />
    );
  }

  return (
    <Box display="flex" alignItems="start" pt={2} px={2} flex={1} minHeight={0}>
      <RfqBucketSidebar counts={counts} selected={bucket} onSelect={selectBucket} />
      <Box ml={3} flex={1} minWidth={0} sx={{ display: "flex", flexDirection: "column", overflow: "hidden", alignSelf: "stretch" }}>
        <Box display="flex" alignItems="baseline" gap={1} mb={2}>
          <Typography component="h2" fontWeight={700} fontSize="16px" lineHeight="24px">
            {`${meta.label} (${totalCount})`}
          </Typography>
        </Box>
        {isFetching && <LinearProgress sx={{ mb: 1, borderRadius: "4px" }} />}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", opacity: isFetching ? 0.5 : 1, transition: "opacity 0.15s" }}>
          {totalCount === 0 && !isFetching ? (
            <Typography color="textSecondary" sx={{ textAlign: "center", py: 6 }}>
              {meta.empty}
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ pb: 2 }}>
              {rows.map((rfq) => (
                <RfqCard key={rfq.id} rfq={rfq} now={now} />
              ))}
            </Stack>
          )}
        </Box>
        {totalPages > 1 && (
          <Box display="flex" justifyContent="flex-end" py={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, next) => setPage(next)}
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: COLOR_CONSTANTS["on-primary/variant"],
                  backgroundColor: COLOR_CONSTANTS["primary/variant"],
                  borderColor: COLOR_CONSTANTS["on-primary/variant"],
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
